import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@atoms'

import { MenuItem } from '../../../../components/common/MenuItem/MenuItem'
import {
    useUsageAndCostLabelsMap,
    useAnalyticsApi,
    UsageCostRequestParams,
} from '../../../../hooks'
import { getColumns } from '../../../../components/data/UsageAndCostTableContainer/getColumns'
import { Dropdown } from '@/components/common/Dropdown/Dropdown'
import { Icon } from '@atoms'
import useAnalyticsStore from '../../stores/useAnalyticsStore/useAnalyticsStore'
import { useState } from 'react'
import useToastStore from '@/stores/useToastStore/useToastStore'
import { extractColumnIds } from './extractColumnIds'

type UsageAndCostExportReportDropdownProps = {
    error?: boolean
    filters: UsageCostRequestParams
}

export const UsageAndCostExportReportDropdown: React.FC<
    UsageAndCostExportReportDropdownProps
> = ({ error, filters }) => {
    const [isOpen, setIsOpen] = useState(false)
    const addAlert = useToastStore((state) => state.addAlert)

    const { useUsageCostQuery } = useAnalyticsApi()
    const { data, isSuccess, downloadXlsx } = useUsageCostQuery(filters)

    const dataSets = useAnalyticsStore((state) => state.usageAndCost.dataSets)

    const labelsMap = useUsageAndCostLabelsMap(filters, true)

    const columnFields = useMemo(() => {
        const columns = getColumns(labelsMap, dataSets)

        return extractColumnIds(columns).map((id) => id.replace(/_/g, '.'))
    }, [dataSets, labelsMap])

    const { t } = useTranslation('translations')

    const noComparableDatasets =
        dataSets.length === 1 && dataSets.includes('date')

    const disabled =
        !isSuccess ||
        !data.items.length ||
        dataSets.length === 0 ||
        noComparableDatasets ||
        error

    return (
        <Dropdown
            isOpen={isOpen}
            onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                setIsOpen(!isOpen)
            }}
            name="usage-and-cost-export-report-dropdown"
            buttonProps={{
                disabled,
                variant: 'outlinePrimary',
                size: 'large',
                name: 'export-dropdown-button',
                children: (
                    <>
                        {t('common.export_report_dropdown.label')}{' '}
                        <Icon size="small" name="keyboard_arrow_down" />
                    </>
                ),
            }}
            buttonComponent={Button}
            horizontalAlign="right"
            dropdownClassname="py-3 !min-w-[auto] z-[50]"
            data-testid="export-report-dropdown"
        >
            <MenuItem
                path="/"
                variant="ghost"
                label={t('common.export_report_dropdown.options.xls')}
                render={({ renderContents }) => (
                    <button
                        className="group w-full outline-none"
                        onClick={() => {
                            addAlert({
                                variant: 'success',
                                title: t(
                                    'reporting.export_report.success_toast.title'
                                ),
                                description: t(
                                    'reporting.export_report.success_toast.description'
                                ),
                            })

                            setIsOpen(false)

                            downloadXlsx(columnFields).catch(() => {
                                addAlert({
                                    variant: 'error',
                                    title: t(
                                        'reporting.export_report.error_toast.title'
                                    ),
                                    description: t(
                                        'reporting.export_report.error_toast.description'
                                    ),
                                })
                            })
                        }}
                        data-testid="export-xls-button"
                    >
                        {renderContents()}
                    </button>
                )}
            />
        </Dropdown>
    )
}
